(() => {
  'use strict';

  // ── Embroidery text field ─────────────────────────────────────────────────

  const embroideryWrapper = document.querySelector('.embroidery-text[data-section-id]');
  if (embroideryWrapper) {
    const { sectionId, triggerOption, excludeValue } = embroideryWrapper.dataset;
    const variantSelectsId = `variant-selects-${sectionId}`;
    const selectName = `options[${triggerOption || 'Embroidery'}]`;

    function updateEmbroidery(select) {
      const input = embroideryWrapper.querySelector('input');
      if (!input) return;
      const active = select.value !== excludeValue;
      embroideryWrapper.hidden = !active;
      input.required = active;
      if (!active) input.value = '';
    }

    // Delegate to document so the listener survives variant-selects DOM replacement
    document.addEventListener('change', (event) => {
      if (
        !event.target.matches(`select[name="${selectName}"]`) ||
        !event.target.closest(`#${variantSelectsId}`)
      ) return;
      updateEmbroidery(event.target);
    });

    const initialSelect = document.querySelector(`#${variantSelectsId} select[name="${selectName}"]`);
    if (initialSelect) updateEmbroidery(initialSelect);
  }

  // ── Add-ons (back pocket, weighted bottom, …) ────────────────────────────
  //
  // Per-section shared state keeps a single price observer, a single stored
  // base price, and a single cart-update subscription so multiple add-on
  // checkboxes batch their items into one /cart/add.js call.

  const sectionState = {};

  class BearsAddon {
    constructor(checkbox) {
      this.checkbox = checkbox;
      this.sectionId = checkbox.dataset.sectionId;
      this.addonVariantId = parseInt(checkbox.dataset.addonVariant, 10);
      this.addonPriceCents = parseInt(checkbox.dataset.addonPrice, 10) || 0;
      this.form = document.getElementById('product-form-' + this.sectionId);
      this.pendingQty = null;

      if (!this.form) return;

      this.productFormEl = this.form.closest('product-form');
      this.dynamicCheckoutContainer = this.productFormEl?.querySelector('[data-shopify="payment-button"]');

      // First add-on for this section initialises shared state + the single cart subscription
      const isFirst = !sectionState[this.sectionId];
      if (isFirst) {
        sectionState[this.sectionId] = {
          priceContainer: document.getElementById('price-' + this.sectionId),
          basePrice: null,
          observer: null,
          instances: [],
        };
        this.setupPriceObserver();
      }
      sectionState[this.sectionId].instances.push(this);

      this.setupCheckoutNotice();
      this.bindEvents();

      // Only subscribe once per section — the handler batches all checked add-ons
      if (isFirst) {
        this.waitForPubSub(() => {
          subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => this.onSectionCartUpdate(data));
        });
      }
    }

    setupCheckoutNotice() {
      if (!this.dynamicCheckoutContainer) return;
      // Reuse a notice already created for this section by a sibling add-on
      const existing = this.dynamicCheckoutContainer.nextElementSibling;
      if (existing?.classList.contains('bears-addon__checkout-notice')) {
        this.notice = existing;
        return;
      }
      this.notice = document.createElement('p');
      this.notice.className = 'bears-addon__checkout-notice caption';
      this.notice.hidden = true;
      this.notice.textContent = 'Use "Add to cart" to include your add-on(s).';
      this.dynamicCheckoutContainer.after(this.notice);
    }

    bindEvents() {
      // Capture phase so we record qty before ProductForm processes submit
      this.form.addEventListener('submit', this.onFormSubmit.bind(this), true);
      this.checkbox.addEventListener('change', this.onCheckboxChange.bind(this));
    }

    // One observer per section — shared across all add-on instances.
    setupPriceObserver() {
      const state = sectionState[this.sectionId];
      if (!state.priceContainer) return;

      state.observer = new MutationObserver(() => {
        // Dawn replaced the price block (variant change) — reset base price
        state.basePrice = null;
        const anyChecked = state.instances.some((inst) => inst.checkbox.checked);
        if (anyChecked) this.updateDisplayedPrice();
      });
      state.observer.observe(state.priceContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    onFormSubmit() {
      if (!this.checkbox.checked) return;
      const qtyInput = document.getElementById('Quantity-' + this.sectionId);
      this.pendingQty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    }

    onCheckboxChange() {
      if (this.dynamicCheckoutContainer) {
        const anyChecked = sectionState[this.sectionId]?.instances.some((inst) => inst.checkbox.checked);
        this.dynamicCheckoutContainer.hidden = anyChecked;
        if (this.notice) this.notice.hidden = !anyChecked;
      }
      this.updateDisplayedPrice();
    }

    updateDisplayedPrice() {
      const state = sectionState[this.sectionId];
      if (!state?.priceContainer) return;

      const priceEl = state.priceContainer.querySelector('.price-item--regular');
      if (!priceEl) return;

      // Capture the true variant base price once; state.basePrice is nulled on re-render
      if (!state.basePrice) {
        state.basePrice = priceEl.textContent.trim();
      }

      const base = state.basePrice;
      const prefix = base.match(/^[^0-9]+/)?.[0] ?? '';
      const suffix = base.match(/[^0-9.]+$/)?.[0] ?? '';
      const baseAmount = parseFloat(base.replace(/[^0-9.]/g, ''));

      // Sum all checked add-ons for this section
      const totalAddedCents = state.instances
        .filter((inst) => inst.checkbox.checked)
        .reduce((sum, inst) => sum + inst.addonPriceCents, 0);

      const newAmount = baseAmount + totalAddedCents / 100;

      state.observer?.disconnect();
      priceEl.textContent = prefix + newAmount.toFixed(2) + suffix;
      state.observer?.observe(state.priceContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Single handler per section — batches all checked add-ons into one cart call
    onSectionCartUpdate(data) {
      if (data?.source === 'bears-addon') return;

      const state = sectionState[this.sectionId];
      const items = state.instances
        .filter((inst) => inst.pendingQty != null)
        .map((inst) => {
          const qty = inst.pendingQty;
          inst.pendingQty = null;
          return { id: inst.addonVariantId, quantity: qty };
        });

      if (!items.length) return;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((r) => r.json())
        .then(() => {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bears-addon' });
          // Re-render the notification button and header bubble
          return fetch('/?sections=cart-notification-button,cart-icon-bubble');
        })
        .then((r) => r.json())
        .then((sectionData) => {
          const parser = new DOMParser();
          ['cart-notification-button', 'cart-icon-bubble'].forEach((id) => {
            const el = document.getElementById(id);
            if (!el || !sectionData[id]) return;
            el.innerHTML = parser.parseFromString(sectionData[id], 'text/html').querySelector('.shopify-section').innerHTML;
          });
        })
        .catch(() => {});
    }

    waitForPubSub(callback) {
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        callback();
      } else {
        requestAnimationFrame(() => this.waitForPubSub(callback));
      }
    }
  }

  document.querySelectorAll('.bears-addon__checkbox').forEach((cb) => new BearsAddon(cb));
})();
